import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import '@chancenlogistik/client/components/logistik-component-shipping-details';
import type { LogistikComponentShippingDetails } from '@chancenlogistik/client/components/logistik-component-shipping-details';
import { LogistikRoutes } from '@chancenlogistik/client/routes';
import { LogistikOrder, LogistikOrderShippingDetails, LogistikOtherOrder } from '@chancenwerk/db/LogistikOrder.js';
import type { ChromiumButton } from '@chancenwerk/design/components/cr-button.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-textarea.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';

/**
 * @element logistik-page-other-order
 */
@customElement('logistik-page-other-order')
export class LogistikPageOtherOrder extends ChemistryLitElement {
  private newOrder = new LogistikOrder({
    shippingDetails: new LogistikOrderShippingDetails({}),
    items: [new LogistikOtherOrder({})],
  });

  @state() private readonly _form = new FormController(this, this.newOrder, {
    success: async order => {
      try {
        await ChromiumDialog.waitFor(order.create());
        void ChromiumDialog.alert('Es wurde erfolgreich handwerkliche Unterstützung angefragt.');
        this.newOrder = new LogistikOrder({
          shippingDetails: new LogistikOrderShippingDetails({}),
          items: [new LogistikOtherOrder({})],
        });
        this._form.document = this.newOrder;
        this._shippingRef.value?.clearSelection();
        this.requestUpdate();
      } catch (e) {
        logger.error(e, 'There was an error completing entire trip order.');
        ChromiumDialog.alert('Es gab ein Problem beim Aufgeben der Bestellung: ' + (e as Error).message);
      }
    },
  });

  @state() private readonly _shippingRef = createRef<LogistikComponentShippingDetails>();

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <logistik-component-base-header
          pageTitle="Benötigst du handwerkliche Unterstützung?"
          backRoute=${LogistikRoutes.landing}
        ></logistik-component-base-header>
        <x-flex space="lg">
          ${InfoText}
          <x-flex space="md">
            <cr-textarea
              placeholder="Beschreibung"
              rows="5"
              ${this._form.field('items', 0, 'description')}
            ></cr-textarea>
            <logistik-component-shipping-details
              ${this._form.field('shippingDetails')}
              ${ref(this._shippingRef)}
            ></logistik-component-shipping-details>
          </x-flex>
          <x-flex horizontal end space="md">
            <cr-button
              icon="send"
              @click=${async (e: MouseEvent) => {
                const currentTarget = e.currentTarget as unknown as ChromiumButton;
                try {
                  await this._shippingRef.value?._form.validate({ mouseEvent: e });
                  await this._form.validate({ mouseEvent: e });
                } catch {
                  currentTarget.loading = false;
                }
              }}
            >
              Bestellung aufgeben
            </cr-button>
          </x-flex>
        </x-flex>
      </logistik-component-base-page-layout>
    `;
  }
}

const InfoText = html`
  <cr-flex space="sm">
    <cr-text> Wackelt dein Schreibtisch? </cr-text>
    <cr-text> Ist deine Glühbirne defekt? </cr-text> <cr-text>Brauchst du einen Nagel in der Wand? </cr-text>
  </cr-flex>
`;
